import { AgelessAssessmentUsageContext } from "@orval/model";

export type AssessmentUsageContextCapabilities = {
  /** When false, every consult/booking surface is removed (cards, bottom bars, sidebars, modal). */
  readonly showConsultCtas: boolean;
  /** When false, promotional/offer/"book your consult" language is replaced with provider-oriented copy. */
  readonly useSalesCopy: boolean;
  /**
   * When true, the patient is physically at the practice: they are treated as
   * inside the service area by default (postal code never auto-marks them
   * outside it). The location question is still shown and the preferred location
   * pre-selected, and they only fall outside the service area if they explicitly
   * decline to travel ("None of these").
   */
  readonly assumeInServiceArea: boolean;
};

export const CONTEXT_PARAM = "context";
export const CONTEXT_LOCATION_PARAM = "locationId";

/**
 * Legacy entry param. Superseded by `context=in_clinic`; a truthy
 * `at_location=true` is still honoured so pre-existing kiosk/front-desk links
 * keep resolving to the In Clinic context. Do not use for new links.
 */
export const LEGACY_AT_LOCATION_PARAM = "at_location";

/**
 * Patient-facing URL wire value for the In Clinic context (`context=in_clinic`).
 * The Admin trackable-link generator emits this same token; parsed into
 * {@link AgelessAssessmentUsageContext.InClinic}. `consult` is intentionally
 * not patient-selectable — the consult flow is initiated from the Admin app.
 */
const IN_CLINIC_PARAM_VALUE = "in_clinic";
export const WEBSITE_EMBED_CONTEXT_PARAM_VALUE = "website_embed";

/**
 * Reads the usage context encoded on the URL. Returns `null` when no (or an
 * unrecognised) `context` param is present so callers can fall back to
 * persisted state or the legacy `at_location` flag.
 */
export function parseUsageContextFromSearchParams(
  searchParams: URLSearchParams,
): AgelessAssessmentUsageContext | null {
  const contextParam = searchParams.get(CONTEXT_PARAM);
  if (contextParam === IN_CLINIC_PARAM_VALUE) {
    return AgelessAssessmentUsageContext.InClinic;
  }

  if (contextParam === WEBSITE_EMBED_CONTEXT_PARAM_VALUE) {
    return AgelessAssessmentUsageContext.WebsiteEmbed;
  }

  return null;
}
